import { useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'

import clsx from 'clsx'
import { useClickAway } from 'react-use'
import useSWR from 'swr'

import { CaseStudyFeedDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { serializeCacheKey } from '@/lib/contentful/utils'

import { isDevelopment } from '@/utils/isDevelopment'

import { Button } from '@/components/Button'
import { Warning } from '@/components/Warning'

import { useCaseStudyFilters } from './useCaseStudyFilters'

type Props = {
	className?: string
	filterButtonLabel?: string
	industryColumnHeader?: string
	topicColumnHeader?: string
	competitorColumnHeader?: string
	cancelButtonLabel?: string
	clearButtonLabel?: string
	applyButtonLabel?: string
	hideInDesktop?: boolean
	resultsLabel?: string
}

export function CaseStudyFilters({
	className,
	filterButtonLabel,
	industryColumnHeader,
	topicColumnHeader,
	competitorColumnHeader,
	cancelButtonLabel,
	clearButtonLabel,
	applyButtonLabel,
	hideInDesktop,
	resultsLabel
}: Props) {
	const { locale, isPreview, query, push } = useRouter()

	const [isOpen, toggle] = useState(false)

	const dropdownRef = useRef<HTMLDivElement>(null)

	const {
		industries,
		topics,
		competitors,
		appliedFilters,
		appliedIndustry,
		appliedTopics,
		appliedCompetitors,
		activeCompetitors,
		activeIndustry,
		activeTopics,
		setActiveIndustry,
		setActiveTopics,
		setActiveCompetitors
	} = useCaseStudyFilters()

	const { data: caseStudyFeedData, error } = useSWR(
		serializeCacheKey('caseStudyFeedTotal', {
			locale,
			industry: appliedIndustry?.title,
			topics: appliedTopics.map((activeTopic) => activeTopic.title),
			competitors: appliedCompetitors.map((activeCompetitor) => activeCompetitor.title)
		}),
		() =>
			client({ preview: isPreview }).request(CaseStudyFeedDocument, {
				where: {
					...(appliedIndustry?.title ? { industriesList: { name: appliedIndustry.title } } : {}),
					...(appliedTopics.length > 0
						? { topicsList: { name_in: appliedTopics.map((activeTopic) => activeTopic.title) } }
						: {}),
					...(appliedCompetitors.length > 0
						? {
								competitorsList: {
									name_in: appliedCompetitors.map((activeCompetitor) => activeCompetitor.title)
								}
						  }
						: {}),
					status: true
				},
				limit: 0,
				skip: 0,
				locale,
				preview: isPreview
			})
	)

	const [total, setTotal] = useState(caseStudyFeedData?.caseStudyCollection?.total ?? 0)

	useEffect(() => {
		setTotal(caseStudyFeedData?.caseStudyCollection?.total ?? 0)
	}, [caseStudyFeedData])

	function updateQueryParams() {
		push(
			{
				query: {
					...query,
					industries: activeIndustry?.slug ?? [],
					topics: activeTopics.map((topic) => topic.slug) ?? [],
					competitors: activeCompetitors.map((competitor) => competitor.slug) ?? []
				}
			},
			undefined,
			{ shallow: true }
		)

		toggle(false)
	}

	function cancelQueryParamsUpdate() {
		setActiveIndustry(appliedFilters.find((filter) => filter.type === 'industry') ?? null)
		setActiveTopics(appliedFilters.filter((filter) => filter.type === 'topic'))
		setActiveCompetitors(appliedFilters.filter((filter) => filter.type === 'competitor'))
		toggle(false)
	}

	function removeSingleFilter(filter: { title: string; slug: string; type: string }) {
		switch (filter.type) {
			case 'industry': {
				setActiveIndustry(null)

				push(
					{
						query: {
							...query,
							industries: []
						}
					},
					undefined,
					{ shallow: true }
				)
				break
			}

			case 'topic': {
				setActiveTopics((prev) => prev.filter((activeTopic) => activeTopic.slug !== filter.slug))

				push(
					{
						query: {
							...query,
							topics:
								typeof query.topics === 'string'
									? []
									: query.topics?.filter((topic) => topic !== filter.slug) ?? []
						}
					},
					undefined,
					{ shallow: true }
				)
				break
			}

			case 'competitor': {
				setActiveCompetitors((prev) =>
					prev.filter((activeCompetitor) => activeCompetitor.slug !== filter.slug)
				)

				push(
					{
						query: {
							...query,
							competitors:
								typeof query.competitors === 'string'
									? []
									: query.competitors?.filter((competitor) => competitor !== filter.slug) ?? []
						}
					},
					undefined,
					{ shallow: true }
				)
				break
			}
		}
	}

	useClickAway(dropdownRef, () => isOpen && cancelQueryParamsUpdate())

	function clearAllFilters() {
		setActiveIndustry(null)
		setActiveTopics([])
		setActiveCompetitors([])
		push(
			{
				query: {
					...query,
					industries: [],
					topics: [],
					competitors: []
				}
			},
			undefined,
			{ shallow: true }
		)
	}

	if (error) {
		if (isDevelopment()) {
			return <Warning>{error?.message || 'An error occurred'}</Warning>
		}
		return null
	}

	return (
		<div className={className}>
			<div className={clsx(hideInDesktop && !isPreview && 'block lg:hidden')}>
				{isOpen && (
					<div className="h-full w-full fixed bg-[#121118] bg-opacity-50 top-0 right-0 z-20 " />
				)}
				<div className=" inline-flex justify-center " ref={dropdownRef}>
					{/* Dropdown */}

					<div
						className={`filter-container w-9/12 md:w-3/5 fixed flex flex-col overflow-auto  gap-7.5 min-w-max shadow-lg left-0 z-20 origin-top  bg-white pb-32  max-overflow-y-auto max-space-y-6   max-z-50 max-inset-0 translate-y-6 bottom-0 top-0 pt-14 ml-auto right-0  transition-all ${
							isOpen ? '' : 'hidden'
						} `}
						role="menu"
						aria-orientation="vertical"
						aria-labelledby="menu-button"
						tabIndex={-1}
					>
						<div className="flex justify-start items-center gap-x-10 col-span-full mt-8  py-8 border-b border-gray-900 px-7.5">
							<Button
								variant="filled"
								size="small"
								color="secondary"
								className="py-0 capitalize"
								style={{ borderRadius: '50px' }}
								data-testid="apply-filters-button"
								onClick={() => {
									updateQueryParams()
									toggle(false)
								}}
							>
								{applyButtonLabel ?? 'Apply'}
							</Button>
							<Button
								variant="outlined"
								size="small"
								color="quaternary"
								className="py-0 capitalize"
								style={{ borderRadius: '50px' }}
								onClick={() => {
									cancelQueryParamsUpdate()
									toggle(false)
								}}
							>
								{cancelButtonLabel ?? 'Cancel'}
							</Button>
						</div>
						{industries.length > 0 && (
							<div className="flex flex-col px-7.5">
								<h5 className="text-[17px] font-medium uppercase text-bc-black">
									{industryColumnHeader ?? 'By Industry'}
								</h5>

								<ul className="flex flex-col items-start gap-y-1.5 mt-5">
									{industries.map((industry, i) => (
										<li key={industry.slug}>
											<input
												type="radio"
												data-slug={industry.slug}
												data-testid="industry-filter"
												id={`industry-filter-${industry.slug}-${i}`}
												onChange={() => setActiveIndustry(industry)}
												checked={activeIndustry?.slug === industry.slug}
												className={clsx('filter-option', 'outline-bc-blue w-5 h-5')}
											/>
											<label
												htmlFor={`industry-filter-${industry.slug}-${i}`}
												className="pl-2 text-gray-600 text-[17px]"
											>
												{industry.title}
											</label>
										</li>
									))}
								</ul>
							</div>
						)}

						{topics.length > 0 && (
							<div className="flex flex-col px-7.5">
								<h5 className="text-[17px] font-medium uppercase text-bc-black">
									{topicColumnHeader ?? 'By Topic'}
								</h5>

								<ul className="flex flex-col items-start gap-y-1.5 mt-5">
									{topics.map((topic, i) => (
										<li key={`topic-filter-${topic.slug}-${i}`}>
											<input
												type="checkbox"
												id={`topic-filter-${topic.slug}-${i}`}
												data-testid="topic-filter"
												onChange={() =>
													setActiveTopics((prev) => {
														if (prev.some((activeTopic) => activeTopic.slug === topic.slug)) {
															return prev.filter((activeTopic) => activeTopic.slug !== topic.slug)
														} else {
															return [...prev, topic]
														}
													})
												}
												checked={activeTopics.some(
													(activeTopic) => activeTopic.slug === topic.slug
												)}
												className={clsx('filter-option', 'outline-bc-blue w-5 h-5')}
											/>
											<label
												htmlFor={`topic-filter-${topic.slug}-${i}`}
												className="pl-2 text-gray-600 text-[17px]"
											>
												{topic.title}
											</label>
										</li>
									))}
								</ul>
							</div>
						)}

						{competitors.length > 0 && (
							<div className="flex flex-col px-7.5">
								<h5 className="text-[17px] font-medium uppercase text-bc-black">
									{competitorColumnHeader ?? 'By Migration Story'}
								</h5>

								<ul className="flex flex-col items-start gap-y-1.5 mt-5">
									{competitors.map((competitor, i) => (
										<li key={`competitor-filter-${competitor.slug}-${i}`}>
											<input
												type="checkbox"
												data-testid="competitor-filter"
												id={`competitor-filter-${competitor.slug}-${i}`}
												onChange={() =>
													setActiveCompetitors((prev) => {
														if (
															prev.some(
																(activeCompetitor) => activeCompetitor.slug === competitor.slug
															)
														) {
															return prev.filter(
																(activeCompetitor) => activeCompetitor.slug !== competitor.slug
															)
														} else {
															return [...prev, competitor]
														}
													})
												}
												checked={activeCompetitors.some(
													(activeCompetitor) => activeCompetitor.slug === competitor.slug
												)}
												className="filter-option outline-bc-blue w-5 h-5"
											/>
											<label
												htmlFor={`competitor-filter-${competitor.slug}-${i}`}
												className="pl-2 text-gray-600 text-[17px]"
											>
												{competitor.title}
											</label>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
				</div>
			</div>
			<div className="flex flex-col gap-5">
				<p className="text-[17px] lg:text-xl font-medium text-bc-black inline-flex items-center">
					{total} {resultsLabel}
				</p>
				<ul className="flex flex-wrap gap-y-2.5 gap-x-2">
					<li data-testid="case-study-applied-filter-btn " className="block lg:hidden">
						<Button
							variant="filled"
							size="small"
							color="secondary"
							onClick={() => toggle((prev) => !prev)}
							className="py-0 capitalize"
							style={{ borderRadius: '50px' }}
						>
							{filterButtonLabel}
						</Button>
					</li>
					{appliedFilters.length > 0 && (
						<>
							{appliedFilters.map((filter, i) => (
								<li
									key={`applied-filter-${filter.slug}-${i}`}
									data-testid="case-study-applied-filter"
								>
									<Button
										variant="outlined"
										size="small"
										color="quaternary"
										data-testid="case-study-applied-filter-remove-button"
										onClick={() => removeSingleFilter(filter)}
										className="py-0 capitalize"
										style={{ borderRadius: '50px' }}
									>
										<span className="inline-flex items-center gap-2">
											{filter.title}
											<svg
												viewBox="0 0 25 25"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
												className="w-2"
											>
												<path
													fillRule="evenodd"
													clipRule="evenodd"
													d="M10.7411 12.1602L0.661133 22.2402L2.58113 24.1602L12.6611 14.0802L22.7411 24.1602L24.6611 22.2402L14.5811 12.1602L24.6611 2.08016L22.7411 0.160156L12.6611 10.2402L2.58113 0.160156L0.661133 2.08016L10.7411 12.1602Z"
													fill="#6D6E7A"
												/>
											</svg>
										</span>
									</Button>
								</li>
							))}

							<li>
								<Button
									variant="outlined"
									size="small"
									color="quaternary"
									onClick={clearAllFilters}
									className="py-0 capitalize"
									style={{ borderRadius: '50px' }}
								>
									{clearButtonLabel}
								</Button>
							</li>
						</>
					)}
				</ul>
			</div>
		</div>
	)
}
